import Debug from 'debug';
import { debounce } from 'lodash';
import * as React from 'react';
import { InputGroup } from 'react-bootstrap';
import {
  Entity,
  Filter,
  getField,
  getFieldPrimitiveFromString,
  getFieldStringFromPrimitive,
  getFieldTypeFilterOptions,
  Primitive,
} from 'gossamer-unified';
import { Dropdown } from '../fields/Dropdown';
import { General } from '../fields/General';
import styles from './SearchCriterion.module.css';

const debug = Debug('gfe/se/SearchCriterion');

interface SearchCriterionProps {
  display: 'full-edit' | 'no-edit' | 'value-edit'; // Omit<FilterDisplay, "hidden">;
  entity: Entity<any>;
  filter: Filter;
  handleChange: (filter: Filter) => void;
  handleDelete: () => void;
}

export const SearchCriterion: React.FC<SearchCriterionProps> = (props) => {
  const [filter, setFilter] = React.useState<Filter>(props.filter);
  const filterRef = React.useRef<Filter>(props.filter);
  React.useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  React.useEffect(() => {
    setFilter(props.filter);
  }, [props.filter.fieldId, props.filter.operator, props.filter.value]);
  if (!props.entity.fields[props.filter.fieldId]) {
    throw new Error(`unrecognized field: ${props.filter.fieldId}`);
  }
  const field = React.useMemo(
    () =>
      Object.assign({}, getField(props.entity, props.filter.fieldId), {
        editable: props.display === 'full-edit',
        mandatory: false,
      }),
    [props.entity, props.filter.fieldId],
  );
  const options = React.useMemo(() => getFieldTypeFilterOptions(field.type), [field.type]);

  const handleOperatorChange = (newValue: string) => {
    const newFilter = Object.assign({}, filter, {
      operator: newValue,
    });
    setFilter(newFilter);
    props.handleChange(newFilter);
  };

  const handleValueChange = React.useMemo(
    () =>
      debounce((newValue: string) => {
        debug(`handleValueChange(${newValue})`);
        const newFilter = Object.assign({}, filterRef.current, {
          value: getFieldPrimitiveFromString(field.type, newValue),
        });
        setFilter(newFilter);
        props.handleChange(newFilter);
      }, 500),
    [field.type, props.handleChange],
  );

  React.useEffect(() => {
    return () => {
      handleValueChange.cancel();
    };
  }, [handleValueChange]);

  return (
    <InputGroup className="mb10px" id={'searchCriterion-' + props.filter.fieldId}>
      {props.display === 'full-edit' && (
        <InputGroup.Text>
          <button onClick={props.handleDelete} className="ControlIcon" type="button">
            ✖
          </button>
        </InputGroup.Text>
      )}
      <InputGroup.Text className="d-none d-md-block">{field.label}</InputGroup.Text>
      {props.display === 'full-edit' && (
        <Dropdown
          className={styles.SearchOperator + ' d-none d-md-block'}
          options={options}
          mandatory={true}
          value={filter.operator}
          onItemSelected={handleOperatorChange}
        />
      )}
      {props.display !== 'full-edit' && (
        <InputGroup.Text>{options.find((opt) => opt.id === filter.operator)?.label}</InputGroup.Text>
      )}
      {!['bl', 'nb', 'in', 'ni'].includes(filter.operator) && props.display !== 'no-edit' && (
        <General
          editable={true}
          field={field}
          fieldId={filter.fieldId}
          handleChange={(newValue) => handleValueChange(newValue || '')}
          // placeholder={`${field.label} ${options.find((opt) => opt.id === filter.operator)?.label}`}
          value={getFieldStringFromPrimitive(field.type, filter.value as Primitive)}
        />
      )}
      {!['bl', 'nb', 'in', 'ni'].includes(filter.operator) && props.display === 'no-edit' && (
        <InputGroup.Text>
          <General
            editable={false}
            field={field}
            fieldId={filter.fieldId}
            handleChange={(newValue) => handleValueChange(newValue || '')}
            // placeholder={`${field.label} ${options.find((opt) => opt.id === filter.operator)?.label}`}
            value={getFieldStringFromPrimitive(field.type, filter.value as Primitive)}
          />
        </InputGroup.Text>
      )}
    </InputGroup>
  );
};
